import type { ReactNode } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import { AuthProvider, useAuth } from "./auth/AuthProvider"
import AppShell from "./components/AppShell"
import { canAccessPath, defaultPathForRole } from "./lib/access"
import BillingPage from "./pages/BillingPage"
import ClientsPage from "./pages/ClientsPage"
import DashboardPage from "./pages/DashboardPage"
import LoginPage from "./pages/LoginPage"
import ProjectDetailPage from "./pages/ProjectDetailPage"
import ProjectsPage from "./pages/ProjectsPage"
import ReportsPage from "./pages/ReportsPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import SalesPage from "./pages/SalesPage"
import SettingsPage from "./pages/SettingsPage"
import TasksPage from "./pages/TasksPage"
import TimeTrackingPage from "./pages/TimeTrackingPage"
import UsersPage from "./pages/UsersPage"

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth()
  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand text-slate-900">
        Loading workspace...
      </div>
    )
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

const RequireAccess = ({ path, children }: { path: string; children: ReactNode }) => {
  const { user, status } = useAuth()
  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand text-slate-900">
        Loading workspace...
      </div>
    )
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />
  }
  if (!canAccessPath(user?.role, path)) {
    return <Navigate to={defaultPathForRole(user?.role)} replace />
  }
  return <>{children}</>
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <RequireAccess path="/">
                <DashboardPage />
              </RequireAccess>
            }
          />
          <Route
            path="clients"
            element={
              <RequireAccess path="/clients">
                <ClientsPage />
              </RequireAccess>
            }
          />
          <Route
            path="projects"
            element={
              <RequireAccess path="/projects">
                <ProjectsPage />
              </RequireAccess>
            }
          />
          <Route
            path="projects/:id"
            element={
              <RequireAccess path="/projects">
                <ProjectDetailPage />
              </RequireAccess>
            }
          />
          <Route
            path="time"
            element={
              <RequireAccess path="/time">
                <TimeTrackingPage />
              </RequireAccess>
            }
          />
          <Route
            path="billing"
            element={
              <RequireAccess path="/billing">
                <BillingPage />
              </RequireAccess>
            }
          />
          <Route
            path="reports"
            element={
              <RequireAccess path="/reports">
                <ReportsPage />
              </RequireAccess>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireAccess path="/tasks">
                <TasksPage />
              </RequireAccess>
            }
          />
          <Route
            path="users"
            element={
              <RequireAccess path="/users">
                <UsersPage />
              </RequireAccess>
            }
          />
          <Route
            path="sales"
            element={
              <RequireAccess path="/sales">
                <SalesPage />
              </RequireAccess>
            }
          />
          <Route
            path="settings"
            element={
              <RequireAccess path="/settings">
                <SettingsPage />
              </RequireAccess>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
